/*
 * Copyright (c) 2019-2021 LogMeIn
 * All Rights Reserved Worldwide.
 *
 * THIS PROGRAM IS CONFIDENTIAL AND PROPRIETARY TO LOGMEIN
 * AND CONSTITUTES A VALUABLE TRADE SECRET.
 */

import { createLogger } from './LoggerService';
import { buildEngagementServiceUrl } from './UrlService';

const log = createLogger('EngagementService');

export interface EngagementData {
  engagementId: string;
  engagementMessage: {
    templateId: string;
    contentId: string;
    headline: string;
    subHeadline: string;
    claims: string[];
    imageURL: string;
    imgAlt: string;
    ctaLinkText: string;
    ctaURL: string;
    subCTAText: string;
    impressionTrackers: string[];
  };
}

export interface AccessToken {
  token_type: string;
  access_token: string;
}

export const gatherEngagementEngineData = (
  meetingId: string = ''
): Promise<EngagementData | null> => {
  return getEngagementEngineData(meetingId).catch((message) => {
    log.error(message);
    return null;
  });
};

const getEngagementEngineData = async (
  meetingId: string
): Promise<EngagementData> => {
  return new Promise((resolve, reject) => {
    const authenticationCode = getAuthenticationCode();
    const req = new XMLHttpRequest();
    req.open('GET', buildEngagementServiceUrl(meetingId));

    req.setRequestHeader('Accept', 'application/json');
    req.setRequestHeader('Content-Type', 'application/json');
    if (authenticationCode) {
      req.setRequestHeader(
        'Authorization',
        `${authenticationCode.token_type} ${authenticationCode.access_token}`
      );
    }

    req.onload = () => {
      if (req.status === 200) {
        const response = JSON.parse(req.response);
        if (response && response.engagementMessage) {
          resolve(response);
        } else {
          reject('Engagement GET request had invalid response');
        }
      } else {
        reject(buildErrorMessage(req.status, 'GET'));
      }
    };
    req.onerror = () => {
      reject('Error: Network Error');
    };
    req.send();
  });
};

export const reportToEngagementEngine = (
  meetingId: string | undefined,
  engagement: EngagementData
): void => {
  postToEngagementEngine(meetingId, engagement).catch((message) =>
    log.error(message)
  );
};

const postToEngagementEngine = async (
  meetingId: string | undefined,
  engagement: EngagementData
) => {
  const url = buildEngagementServiceUrl(meetingId);
  const payload = {
    engagementId: engagement.engagementId,
    engagementMessage: {
      contentId: engagement.engagementMessage.contentId,
      ctaClicked: true,
    },
  };
  return new Promise((resolve, reject) => {
    const req = new XMLHttpRequest();
    const authenticationCode = getAuthenticationCode();
    req.open('POST', url);
    req.setRequestHeader('Accept', 'application/json');
    req.setRequestHeader('Content-Type', 'application/json');
    if (authenticationCode) {
      req.setRequestHeader(
        'Authorization',
        `${authenticationCode.token_type} ${authenticationCode.access_token}`
      );
    }
    req.onload = () => {
      if (req.status === 200) {
        resolve(req.response);
      } else {
        reject(buildErrorMessage(req.status, 'POST'));
      }
    };
    req.onerror = () => {
      reject('Error: Network Error');
    };
    req.send(JSON.stringify(payload));
  });
};

export const getAuthenticationCode = (): AccessToken | null => {
  const cookieName = '__Host-webAppAccessToken';
  const match = document.cookie.match(
    `(^|[^;]+)\\s*${cookieName}\\s*=\\s*([^;]+)`
  );
  const encodedToken = match ? match.pop() : null;
  return decodeToken(encodedToken);
};

const decodeToken = (token: any): AccessToken | null => {
  if (token) {
    try {
      const decodedToken = atob(token);
      return JSON.parse(decodedToken);
    } catch (e) {
      log.error('Access token could not be parsed.');
    }
  }
  return null;
};

export const buildErrorMessage = (
  status: number,
  requestType: string
): string => {
  let resultString = `Error ${status}: `;
  switch (status) {
    case 400:
      resultString += `Engagement ${requestType} request failed due to bad request`;
      break;
    case 401:
      resultString += `Engagement ${requestType} request failed because authorization token is expired or the user is not allowed to see ads`;
      break;
    case 404:
      resultString += `Engagement ${requestType} request responded with no content found for account`;
      break;
    default:
      resultString += `Engagement ${requestType} request failed unexpectedly`;
      break;
  }
  return resultString;
};
